import { useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { faCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';

import { PlatformApp } from '@typings';
import { platformAppsVersionsSelector } from '@selectors';
import { useSelector } from '@hooks';
import { formatToCamelCase, path } from '@utils';

import { Button, Icon, Link, Popover, Render } from '@components/Shared';

type Props = {
  pubDate: string;
  latestVersion: string;
  version: string;
};

export const PlatformAppTemplateVersion: React.FC<Props> = ({
  pubDate,
  latestVersion,
  version: templateVersion,
}) => {
  const { templates: versionTemplates } = useSelector(
    platformAppsVersionsSelector,
  );

  const { appName } = useParams();

  const [open, setOpen] = useState(false);

  const templates = formatToCamelCase(
    versionTemplates,
  ) as PlatformApp.Template[];
  const isLatest = latestVersion === templateVersion;
  const versioning = templates.length > 1;

  const formatDate = (date: string) => dayjs(date).local().format('LL');

  const makeVersion = ({ version, pubDate }: PlatformApp.Template) => (
    <Button
      theme
      key={version}
      className="block text-left [&:nth-child(2)]:border-b [&:nth-child(2)]:border-neural-02"
      onClick={() => setOpen(false)}
    >
      <Link
        theme
        replace
        to={path.exploreApp(appName, encodeURIComponent(version))}
        className="group flex items-center gap-2 px-5 py-2"
      >
        <div className="flex-1">
          <p className="transition-colors group-hover:text-primary">
            {version}
          </p>
          <p className="text-body-small text-neural-04">
            {formatDate(pubDate)}
          </p>
        </div>
        <Render if={version === templateVersion}>
          <Icon icon={faCheck} className="text-primary" />
        </Render>
      </Link>
    </Button>
  );

  return (
    <Popover
      open={open}
      side="bottom"
      align="start"
      disabled={!versioning}
      onOpenChange={setOpen}
      className="px-0 pb-2 pt-5"
    >
      <div slot="trigger" className="relative mt-6 flex items-center gap-1.5">
        <p className="text-body-medium">
          Version: {templateVersion}{' '}
          <span className="text-neural-04">({formatDate(pubDate)})</span>
        </p>
        <Render if={versioning}>
          <Icon
            icon={faChevronDown}
            className="h-4 w-4 -translate-y-px text-[12px]"
          />
        </Render>
        <Render if={isLatest}>
          <div className="absolute left-full top-1/2 flex h-6 w-[122px] -translate-y-1/2 translate-x-1.5 items-center justify-center gap-1 rounded-xl bg-neural-01 py-1 pl-2 pr-3 text-body-small text-black">
            <Icon icon={faSparkles} className="h-4 w-4 text-neural-04" />
            Latest version
          </div>
        </Render>
      </div>
      <div className="flex min-w-[264px] flex-col">
        <p className="px-5 text-body-small uppercase text-neural-04">
          Latest version
        </p>
        {templates.map(makeVersion)}
      </div>
    </Popover>
  );
};
